// AGC: Reusable player progression panel component
// AGC: Used in Profile.tsx, PVEBattle.tsx, and P2PBattle.tsx
import React from 'react'
import { formatOgbBalanceDisplay } from '../utils/baseAppUtils'
import { useAccount } from 'wagmi'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { usePlayerProgression } from '../hooks/usePlayerProgression'

interface PlayerProgressionPanelProps {
  title: string // AGC: Page title (e.g., "PLAYER", "QUICK BATTLE", "P2P BATTLE")
  subtitle?: string // AGC: Optional subtitle (e.g., "player" for P2P Battle)
  playerAddress?: string // AGC: Optional player address (if not provided, uses connected wallet)
  walletAddress?: string // AGC: Optional wallet address for BaseScan link (Profile only)
  connectionStatus?: {
    isConnected: boolean
    label: string
  } // AGC: Optional connection status (for battle pages)
  fallbackLevel?: number // AGC: Optional fallback level if playerStats not available
}

/**
 * AGC: Player progression panel showing level, stats, balance, and XP progress
 * AGC: Reusable across Profile, QuickBattle, and P2PBattle pages
 * AGC: Automatically fetches player data from hooks
 */
export const PlayerProgressionPanel: React.FC<PlayerProgressionPanelProps> = ({
  title,
  subtitle,
  playerAddress,
  walletAddress,
  connectionStatus,
  fallbackLevel,
}) => {
  // AGC: Get connected address if playerAddress not provided
  const { address: connectedAddress } = useAccount()
  const effectiveAddress = (playerAddress || connectedAddress) as `0x${string}` | undefined

  // AGC: Fetch player stats and OGB balance
  const { playerStats } = usePlayerProgression(effectiveAddress || '')
  const { balance: ogbBalance } = usePlayerStatus(effectiveAddress)

  // AGC: Calculate player stats
  const playerLevel = playerStats?.level || fallbackLevel || 1
  const playerAttack = playerStats?.attack ?? 0
  const playerDefense = playerStats?.defense ?? 0
  const currentXP = playerStats?.experience ?? 0
  const xpToNext = playerStats?.experienceToNext ?? 0
  const xpTarget = currentXP + Math.max(0, xpToNext)
  const xpProgress = xpTarget > 0 ? Math.min(1, currentXP / xpTarget) : 0

  // AGC: Format display values
  const ogbBalanceString = ogbBalance.toString()
  const ogbBalanceDisplay = formatOgbBalanceDisplay(ogbBalanceString)
  const walletShort = walletAddress ? `${walletAddress.slice(0, 4)}…${walletAddress.slice(-4)}` : undefined
  const formattedXpRange = `${currentXP.toLocaleString()} / ${xpTarget.toLocaleString()}`

  return (
    <section data-tour="battle-player-progression" className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800 rounded-2xl p-4 shadow-[0_15px_40px_rgba(0,0,0,0.5)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase text-slate-400 tracking-[0.2em]">{title}</p>
          {subtitle && (
            <p className="text-[10px] uppercase text-slate-400 tracking-[0.2em] mt-0.5">{subtitle}</p>
          )}
          <h1 className="text-xl font-bold">Lv {playerLevel} • {walletShort || `${ogbBalanceDisplay} OGB`}</h1>
          <p className="text-xs text-slate-400">Attack {playerAttack} • Defense {playerDefense}</p>
        </div>
        <div className="flex items-center gap-2">
          {walletAddress && (
            <a
              href={`https://sepolia.basescan.org/address/${walletAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] px-3 py-1 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 transition-colors"
            >
              View on BaseScan
            </a>
          )}
          {connectionStatus && (
            <div className="rounded-lg bg-white/10 border border-white/10 px-3 py-1 text-[10px] font-semibold">
              <span className={`flex items-center gap-1 leading-none ${connectionStatus.isConnected ? 'text-emerald-300' : 'text-amber-300'}`}>
                <span
                  className={`h-2 w-2 rounded-full ${connectionStatus.isConnected ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`}
                ></span>
                {connectionStatus.label}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
          <span>XP Progress</span>
          <span>{formattedXpRange}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 transition-all"
            style={{ width: `${xpProgress * 100}%` }}
          />
        </div>
      </div>
    </section>
  )
}
